/**
 * Hooks de **lectura** del dominio Pedidos de láminas (TanStack Query).
 *
 * Datos de servidor -> TanStack Query (`docs/frontend_conventions.md`, Datos): no
 * `useState`+`useEffect`+`fetch`. La red la gobierna `lib/api` (cookie httpOnly +
 * `X-Sede-Id`). Cada query se cachea por sede activa (en la key) para recargarse al
 * cambiar de sede. El `enabled` permite no pedir el detalle hasta que se necesita.
 */
import { useQuery } from "@tanstack/react-query";

import { getActiveSedeId } from "@/lib/sede";

import { fetchBoard, fetchHistorial, fetchPedido } from "../api";
import type { PedidoDetail, PedidosBoard, PedidosHistorial } from "../types";
import {
  boardQueryKey,
  historialQueryKey,
  pedidoDetailQueryKey,
} from "./queryKeys";

/** Margen corto: el tablero cambia a menudo (altas/transiciones), sin refetch agresivo. */
const STALE_TIME = 15_000;

/** Tablero de pedidos de la sede activa (`GET /pedidos-laminas`). */
export function usePedidosBoard() {
  const sedeId = getActiveSedeId();
  return useQuery<PedidosBoard>({
    queryKey: boardQueryKey(sedeId),
    queryFn: fetchBoard,
    staleTime: STALE_TIME,
  });
}

/** Historial de recibidos de la sede activa (`GET /pedidos-laminas/historial`). */
export function usePedidosHistorial() {
  const sedeId = getActiveSedeId();
  return useQuery<PedidosHistorial>({
    queryKey: historialQueryKey(sedeId),
    queryFn: fetchHistorial,
    staleTime: STALE_TIME,
  });
}

/**
 * Detalle de un pedido (`GET /pedidos-laminas/<id>`). Solo se pide con un id válido;
 * la pantalla de edición lo usa para prellenar el formulario.
 */
export function usePedido(id: number | null) {
  const sedeId = getActiveSedeId();
  return useQuery<PedidoDetail>({
    queryKey: pedidoDetailQueryKey(sedeId, id ?? 0),
    queryFn: () => fetchPedido(id as number),
    enabled: id !== null && id > 0,
    staleTime: STALE_TIME,
  });
}
